import React from 'react';

const PALETTES = {
  'art-appreciation': {
    base: ['#fce8ef', '#f5d4e8', '#e8ddf5', '#fff0e8'],
    strokes: ['#f4a4b8', '#e8a8d4', '#c9b8f0', '#ffc9b0', '#ffb8c8'],
  },
  'ways-of-seeing': {
    base: ['#e4f5f0', '#dff0fa', '#f0f8e4', '#e8f4fc'],
    strokes: ['#8ed4c4', '#9ec8f0', '#b8e6a8', '#7ec8e8', '#a8e0d0'],
  },
  'reimagine-artwork': {
    base: ['#f0e8fc', '#fce8f4', '#e8f0fc', '#fff4e8'],
    strokes: ['#c4a8f0', '#f0a8d8', '#a8c8f8', '#f8c8a8', '#d8b0f0'],
  },
  'learn-art-history': {
    base: ['#fff8e8', '#ffeccd', '#f5e8d4', '#fff4e0'],
    strokes: ['#f0c878', '#e8b060', '#d4a858', '#ffc878', '#f5d8a0'],
  },
  'co-learning-museum': {
    base: ['#fff9ef', '#ffefd6', '#fde8c8', '#fffdf8'],
    strokes: ['#f59e0b', '#fbbf24', '#ea580c', '#fcd34d', '#fdba74'],
  },
  'general-exhibit': {
    base: ['#f5f0ff', '#fce8f4', '#e8f4fc', '#fff8ef'],
    strokes: ['#c4a8f0', '#f0a8d8', '#9ec8f0', '#f8c8a8', '#d8b0f0'],
  },
};

/**
 * Pastel impasto paint texture for Artist Corner feature card fronts.
 */
export default function ImpastoPastelThumb({ variant = 'art-appreciation', className = '' }) {
  const palette = PALETTES[variant] || PALETTES['art-appreciation'];
  const id = `impasto-${variant}`;

  return (
    <div className={`impasto-pastel-thumb absolute inset-0 overflow-hidden ${className}`} aria-hidden>
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" viewBox="0 0 600 400">
        <defs>
          <linearGradient id={`${id}-bg`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={palette.base[0]} />
            <stop offset="35%" stopColor={palette.base[1]} />
            <stop offset="68%" stopColor={palette.base[2]} />
            <stop offset="100%" stopColor={palette.base[3]} />
          </linearGradient>
          <filter id={`${id}-grain`} x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="4" stitchTiles="stitch" result="noise" />
            <feColorMatrix type="saturate" values="0" in="noise" result="mono" />
            <feBlend in="SourceGraphic" in2="mono" mode="soft-light" />
          </filter>
          <filter id={`${id}-soft`}>
            <feGaussianBlur stdDeviation="2.4" />
          </filter>
          <filter id={`${id}-impasto`}>
            <feGaussianBlur in="SourceGraphic" stdDeviation="0.6" result="blur" />
            <feSpecularLighting in="blur" surfaceScale="3" specularConstant="0.55" specularExponent="18" lightingColor="#fffef8" result="spec">
              <fePointLight x="120" y="60" z="180" />
            </feSpecularLighting>
            <feComposite in="spec" in2="SourceGraphic" operator="arithmetic" k1="0" k2="1" k3="0.35" k4="0" />
          </filter>
        </defs>

        <rect width="600" height="400" fill={`url(#${id}-bg)`} />

        {/* Thick impasto strokes — layered ellipses with heavy blur */}
        <g filter={`url(#${id}-impasto)`} opacity="0.95">
          <ellipse cx="120" cy="95" rx="95" ry="62" fill={palette.strokes[0]} transform="rotate(-18 120 95)" />
          <ellipse cx="480" cy="110" rx="88" ry="55" fill={palette.strokes[1]} transform="rotate(22 480 110)" />
          <ellipse cx="310" cy="200" rx="130" ry="78" fill={palette.strokes[2]} transform="rotate(-8 310 200)" />
          <ellipse cx="90" cy="290" rx="75" ry="48" fill={palette.strokes[3]} transform="rotate(12 90 290)" />
          <ellipse cx="500" cy="300" rx="100" ry="58" fill={palette.strokes[4]} transform="rotate(-25 500 300)" />
          <ellipse cx="380" cy="70" rx="55" ry="38" fill={palette.strokes[0]} opacity="0.75" transform="rotate(35 380 70)" />
          <ellipse cx="200" cy="320" rx="85" ry="50" fill={palette.strokes[1]} opacity="0.8" transform="rotate(-5 200 320)" />
        </g>

        {/* Palette-knife highlights */}
        <g opacity="0.45">
          <path
            d="M 40 180 Q 140 120 220 160 T 380 140"
            fill="none"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="14"
            strokeLinecap="round"
          />
          <path
            d="M 250 280 Q 350 220 520 260"
            fill="none"
            stroke="rgba(255,255,255,0.7)"
            strokeWidth="10"
            strokeLinecap="round"
          />
        </g>

        {/* Canvas grain */}
        <rect width="600" height="400" fill="#ffffff" opacity="0.12" filter={`url(#${id}-grain)`} />
      </svg>

      <div
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage: `repeating-linear-gradient(
            105deg,
            transparent,
            transparent 2px,
            rgba(255,255,255,0.12) 2px,
            rgba(255,255,255,0.12) 3px
          )`,
        }}
      />
    </div>
  );
}

